import { useState, useEffect } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { Button } from './Button';
import { Modal } from './Modal';
import { useToast } from '../hooks/useToast';
import { useLanguage } from '../hooks/useLanguage';
import {
  getActivities,
  createActivity,
  updateActivity,
  deleteActivity
} from '../services/api';

export const ActivitiesManager = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [formData, setFormData] = useState({
    activity_name: '',
    display_order: 0,
    parent_activity_id: null,
  });
  const toast = useToast();
  const { t, language } = useLanguage();

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const data = await getActivities();
      setActivities(data);
    } catch (error) {
      console.error('Failed to load activities:', error);
      toast.error(t('failedToLoadActivities') || 'Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (activity = null) => {
    if (activity) {
      setEditingActivity(activity);
      setFormData({
        activity_name: activity.activity_name,
        display_order: activity.display_order,
        parent_activity_id: activity.parent_activity_id || null,
      });
    } else {
      setEditingActivity(null);
      setFormData({
        activity_name: '',
        display_order: activities.length,
        parent_activity_id: null,
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingActivity(null);
    setFormData({
      activity_name: '',
      display_order: 0,
      parent_activity_id: null,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingActivity) {
        await updateActivity(editingActivity.id, formData);
        toast.success(t('activityUpdated') || 'Activity updated successfully');
      } else {
        await createActivity(formData);
        toast.success(t('activityCreated') || 'Activity created successfully');
      }

      handleCloseModal();
      fetchActivities();
    } catch (error) {
      const errorCode = error.response?.data?.error?.code;
      if (errorCode === 'ACTIVITY_ALREADY_EXISTS') {
        toast.error(t('activityAlreadyExists') || 'An activity with this name already exists');
      } else {
        toast.error(t('failedToSaveActivity') || 'Failed to save activity');
      }
    }
  };

  const handleDelete = async (activity) => {
    if (!confirm(t('deleteActivityConfirm') || `Are you sure you want to delete "${activity.activity_name}"?`)) {
      return;
    }

    try {
      await deleteActivity(activity.id);
      toast.success(t('activityDeleted') || 'Activity deleted successfully');
      fetchActivities();
    } catch (error) {
      const errorCode = error.response?.data?.error?.code;
      if (errorCode === 'ACTIVITY_HAS_SUBCATEGORIES') {
        toast.error(t('cannotDeleteActivityWithSubcategories') || 'Cannot delete activity that has sub-categories. Delete sub-categories first');
      } else if (errorCode === 'ACTIVITY_HAS_EVENTS') {
        toast.error(t('cannotDeleteActivityWithEvents') || 'Cannot delete activity that has associated events');
      } else if (errorCode === 'ACTIVITY_HAS_LEADS') {
        toast.error(t('cannotDeleteActivityWithLeads') || 'Cannot delete activity that has associated leads');
      } else {
        toast.error(t('activityDeleteFailed') || 'Failed to delete activity');
      }
    }
  };

  const handleToggleActive = async (activity) => {
    try {
      await updateActivity(activity.id, {
        is_active: !activity.is_active
      });
      fetchActivities();
    } catch (error) {
      toast.error(t('failedToUpdateActivity') || 'Failed to update activity');
    }
  };

  // Get parent activities (top-level categories)
  const getParentActivities = () => {
    return activities.filter(activity => !activity.parent_activity_id);
  };

  // Get sub-categories for a parent activity
  const getSubCategories = (parentId) => {
    return activities.filter(activity => activity.parent_activity_id === parentId);
  };

  // Get all activities in hierarchical order for display
  const getHierarchicalActivities = () => {
    const result = [];
    const parentActivities = getParentActivities();

    parentActivities.forEach(parent => {
      result.push({ ...parent, level: 0 });
      const children = getSubCategories(parent.id);
      children.forEach(child => {
        result.push({ ...child, level: 1 });
      });
    });

    return result;
  };

  const hierarchicalActivities = getHierarchicalActivities();

  if (loading) {
    return <div className="text-center py-8">{t('loading')}</div>;
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
            {t('activities') || 'Activities'}
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            {t('manageActivities') || 'Manage campaign activities and categories'}
          </p>
        </div>
        <Button onClick={() => handleOpenModal()} className="w-full sm:w-auto">
          <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
            <path d="M12 4v16m8-8H4" />
          </svg>
          {t('addActivity') || 'Add Activity'}
        </Button>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {hierarchicalActivities.length === 0 ? (
          <div className="bg-white shadow-md rounded-lg p-6 text-center text-gray-500">
            {t('noActivities') || 'No activities found'}
          </div>
        ) : (
          hierarchicalActivities.map((activity) => (
            <div
              key={activity.id}
              className="bg-white shadow-md rounded-lg p-4 space-y-3"
              style={{ marginLeft: activity.level === 1 ? '1rem' : '0' }}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 flex-1">
                  {activity.level === 1 && (
                    <span className="text-gray-400">└─</span>
                  )}
                  <span className="text-sm font-semibold text-gray-500">#{activity.display_order}</span>
                  <div>
                    <h3 className={`text-lg font-semibold ${activity.level === 1 ? 'text-gray-700' : 'text-gray-900'}`}>
                      {activity.activity_name}
                    </h3>
                    {activity.level === 1 && (
                      <span className="text-xs text-gray-500">{t('subCategory') || 'Sub-category'}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleToggleActive(activity)}
                  className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                    activity.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {activity.is_active ? t('active') || 'Active' : t('inactive') || 'Inactive'}
                </button>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t">
                <button
                  onClick={() => handleOpenModal(activity)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title={t('edit') || 'Edit'}
                >
                  <Pencil className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDelete(activity)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title={t('delete') || 'Delete'}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                {t('name') || 'Name'}
              </th>
              <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                {t('displayOrder') || 'Display Order'}
              </th>
              <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                {t('status') || 'Status'}
              </th>
              <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'he' ? 'text-right' : 'text-left'}`}>
                {t('actions') || 'Actions'}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {hierarchicalActivities.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                  {t('noActivities') || 'No activities found'}
                </td>
              </tr>
            ) : (
              hierarchicalActivities.map((activity) => (
                <tr key={activity.id} className={activity.level === 1 ? 'bg-gray-50' : ''}>
                  <td className={`px-6 py-4 whitespace-nowrap ${language === 'he' ? 'text-right' : 'text-left'}`}>
                    <div className="flex items-center gap-2">
                      {activity.level === 1 && (
                        <span className="text-gray-400">└─</span>
                      )}
                      <div className={`text-sm font-medium ${activity.level === 1 ? 'text-gray-700' : 'text-gray-900'}`}>
                        {activity.activity_name}
                        {activity.level === 1 && (
                          <span className="ml-2 text-xs text-gray-500">({t('subCategory') || 'Sub-category'})</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap ${language === 'he' ? 'text-right' : 'text-left'}`}>
                    <div className="text-sm text-gray-600">
                      {activity.display_order}
                    </div>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap ${language === 'he' ? 'text-right' : 'text-left'}`}>
                    <button
                      onClick={() => handleToggleActive(activity)}
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        activity.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {activity.is_active ? t('active') || 'Active' : t('inactive') || 'Inactive'}
                    </button>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${language === 'he' ? 'text-right' : 'text-left'}`}>
                    <div className={`flex items-center gap-2 ${language === 'he' ? 'justify-end' : 'justify-start'}`}>
                      <button
                        onClick={() => handleOpenModal(activity)}
                        className="text-blue-600 hover:text-blue-900"
                        title={t('edit') || 'Edit'}
                      >
                        <Pencil className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(activity)}
                        className="text-red-600 hover:text-red-900"
                        title={t('delete') || 'Delete'}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={editingActivity ? (t('editActivity') || 'Edit Activity') : (t('addActivity') || 'Add Activity')}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('activityName') || 'Activity Name'}
            </label>
            <input
              type="text"
              value={formData.activity_name}
              onChange={(e) => setFormData({ ...formData, activity_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              placeholder={t('enterActivityName') || 'Enter activity name'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('parentCategory') || 'Parent Category'} ({t('optional') || 'Optional'})
            </label>
            <select
              value={formData.parent_activity_id || ''}
              onChange={(e) => setFormData({ ...formData, parent_activity_id: e.target.value ? parseInt(e.target.value) : null })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={editingActivity && getSubCategories(editingActivity.id).length > 0}
            >
              <option value="">{t('topLevelCategory') || 'Top-level category (no parent)'}</option>
              {getParentActivities()
                .filter(activity => !editingActivity || activity.id !== editingActivity.id)
                .map(activity => (
                  <option key={activity.id} value={activity.id}>
                    {activity.activity_name}
                  </option>
                ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {t('parentCategoryHelp') || 'Select a parent category to create a sub-category'}
            </p>
            {editingActivity && getSubCategories(editingActivity.id).length > 0 && (
              <p className="mt-1 text-xs text-amber-600">
                {t('cannotChangeParentWithSubcategories') || 'Cannot change parent while this activity has sub-categories'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('displayOrder') || 'Display Order'}
            </label>
            <input
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="0"
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('displayOrderHelp') || 'Lower numbers appear first'}
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" variant="primary" className="flex-1">
              {editingActivity ? (t('update') || 'Update') : (t('create') || 'Create')}
            </Button>
            <Button type="button" variant="secondary" onClick={handleCloseModal} className="flex-1">
              {t('cancel') || 'Cancel'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
